// AdminTagsList.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { tagService } from '../../../services/tagService';
import './AdminCategories.css';

/**
 * Компонент для отображения списка тегов с возможностью
 * редактирования, удаления и добавления новых тегов
 */

const AdminTagsList = () => {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /**
   * Загрузка списка тегов
   */
  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tagService.getAllTags();
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ошибка загрузки тегов:', error);
      setError(error.message || 'Не удалось загрузить список тегов');
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  /**
   * Обработчик удаления тега
   * @param {number} id - ID тега
   * @param {string} name - Название тега
   */
  const handleDeleteTag = async (id, name) => {
    const isConfirmed = window.confirm(
      `Вы уверены, что хотите удалить тег "${name}"?\n\n` +
      `Это действие нельзя отменить.`
    );

    if (!isConfirmed) return;

    try {
      setDeletingId(id);
      await tagService.deleteTag(id);

      setTags(prevTags => prevTags.filter(tag => tag.id !== id));
      alert('Тег успешно удален');
    } catch (error) {
      console.error('Ошибка удаления:', error);
      alert(error.message || 'Не удалось удалить тег');
    } finally {
      setDeletingId(null);
    }
  };

  /**
   * Получение CSS класса для цвета тега
   * @param {string} color - Название цвета
   * @returns {string} CSS класс
   */
  const getColorBadgeClass = (color) => {
    switch (color?.toLowerCase()) {
      case 'синий':
        return 'color-blue';
      case 'красный':
        return 'color-red';
      case 'желтый':
        return 'color-yellow';
      default:
        return 'color-default';
    }
  };

  // Состояние загрузки
  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading__spinner"></div>
        <p>Загрузка тегов...</p>
      </div>
    );
  }

  // Состояние ошибки
  if (error) {
    return (
      <div className="admin-error">
        <div className="admin-error__icon">⚠️</div>
        <p className="admin-error__message">{error}</p>
        <button 
          onClick={fetchTags} 
          className="admin-error__retry"
        >
          Повторить попытку
        </button>
      </div>
    );
  }

  // Пустое состояние
  if (tags.length === 0) {
    return (
      <div className="admin-categories-list__empty">
        <div className="admin-empty-state__icon">🏷️</div>
        <h3>Теги не найдены</h3>
        <p>Создайте первый тег, чтобы начать работу</p>
        <button 
          className="admin-categories-list__add-btn"
          onClick={() => navigate('/admin/tags/add')}
        >
          + Создать первый тег
        </button>
      </div>
    );
  }

  return (
    <div className='admin-categories-list'>
      {/* Заголовок с кнопкой добавления */}
      <div className="admin-categories-list__header">
        <div className="admin-categories-list__title-section">
          <h2>Управление тегами</h2>
          <span className="admin-categories-list__count">
            Всего: {tags.length} {getDeclension(tags.length)}
          </span>
        </div>
        <button 
          className="admin-categories-list__add-btn"
          onClick={() => navigate('/admin/tags/add')}
        >
          <span className="btn-icon">+</span>
          Добавить тег
        </button>
      </div>

      {/* Таблица тегов */}
      <div className="admin-categories-list__table-wrapper">
        <table className="admin-categories-list__table">
          <thead>
            <tr>
              <th className="col-id">ID</th>
              <th className="col-name">Название</th>
              <th className="col-color">Цвет</th>
              <th className="col-actions">Действия</th>
            </tr>
          </thead>
          <tbody>
            {tags.map(tag => (
              <tr key={tag.id}>
                <td className="col-id">{tag.id}</td>
                <td className="col-name">
                  <strong>{tag.name}</strong>
                </td>
                <td className="col-color">
                  <span className={`color-badge ${getColorBadgeClass(tag.color)}`}>
                    {tag.color}
                  </span>
                </td>
                <td className="col-actions">
                  <button 
                    className="action-btn edit-btn"
                    onClick={() => navigate(`/admin/tags/edit/${tag.id}`)}
                    title="Редактировать тег"
                  >
                    ✏️ Редактировать
                  </button>
                  <button 
                    className="action-btn delete-btn"
                    onClick={() => handleDeleteTag(tag.id, tag.name)}
                    disabled={deletingId === tag.id}
                    title="Удалить тег"
                  >
                    {deletingId === tag.id ? '⏳' : '🗑️ Удалить'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Вспомогательная функция для склонения слова "тег"
 * @param {number} count - Количество тегов
 * @returns {string} Правильная форма слова
 */
function getDeclension(count) {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'тегов';
  }
  
  if (lastDigit === 1) {
    return 'тег';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'тега';
  }
  
  return 'тегов';
}

export default AdminTagsList;